<script lang="ts" setup>
import { ref, computed, onMounted, onBeforeUnmount, reactive, getCurrentInstance } from 'vue';
import { type AppInitializationDto, AuthenticationState } from '@mit/aws-vue';
import { AuthProvider as AuthProviderV2, IS_OFFLINE } from '@/common/auth/authProvider';
import ConfirmDialog from 'primevue/confirmdialog';
import CustomToast from './common/components/CustomToast.vue';
import AppSidebar from './common/components/AppSidebar.vue';
import AppHeader from './common/components/AppHeader.vue';
import OfflineIdentityPicker from './common/components/OfflineIdentityPicker.vue';
import { useAuthStore } from './stores/authStore';
import {
  ensureFreshToken,
  isSessionExpired,
  onSessionExpired,
  retrySignIn,
} from '@/common/composables/useAuthGuard';
import { STAGE_CONFIG } from './config';
import { useMitLogo } from '@/common/composables/useMitLogo';
import { useNotificationStore } from '@/stores/notificationStore';
import { PARLEY_DOCS_URL, getRagDocsUrl } from '@/common/utils/serviceLinks';
import {
  claimAccountWelcome,
  claimRagAnnouncement,
  ragAnnouncementSeen,
  welcomeAllowed,
} from '@/common/composables/usePendingWelcome';
import { fetchKnowledgeBaseEnabled } from '@/common/composables/useKnowledgeBaseFlag';
import MyAccountsWelcomeDialog from '@/features/account-admin/components/MyAccountsWelcomeDialog.vue';
import RagAnnouncementDialog from '@/features/rag/components/RagAnnouncementDialog.vue';

const customToastRef = ref();
const instance = getCurrentInstance();
const authStore = useAuthStore();
const notificationStore = useNotificationStore();
const { url: mitLogo, inverted: mitLogoInverted } = useMitLogo();

const PARLEY_SUPPORT_EMAIL = 'parley-support@mit.edu';
const PARLEY_SUPPORT_MAILTO = `mailto:${PARLEY_SUPPORT_EMAIL}?subject=Parley%20access`;

onMounted(() => {
  if (instance && customToastRef.value) {
    instance.appContext.config.globalProperties.$customToast = customToastRef.value;
  }
});

onMounted(() => {
  document.addEventListener('visibilitychange', handleVisibilityChange);
  unsubSessionExpired = onSessionExpired(() => {
    sessionExpired.value = true;
    isLoggedIn.value = false;
  });
});

onBeforeUnmount(() => {
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  unsubSessionExpired?.();
});

const initializeDto = reactive<AppInitializationDto>({
  isLoading: true,
  hasAccess: false,
  hasError: false,
});

const isLoggedIn = ref(false);
const sidebarCollapsed = ref(false);
const sessionExpired = ref(isSessionExpired());
const signingIn = ref(false);
const loadingStatus = ref('Authenticating...');
const minTimeElapsed = ref(false);

let unsubSessionExpired: (() => void) | null = null;

function handleSignIn() {
  if (signingIn.value) return;
  signingIn.value = true;
  void retrySignIn();
}

function handleVisibilityChange() {
  if (document.visibilityState !== 'visible') return;
  if (!isLoggedIn.value) return;
  if (sessionExpired.value) return;
  // Force a token check; the composable triggers a (deduped) Okta redirect
  // if the token can't be renewed. No need to await — failure is handled
  // via the onSessionExpired listener.
  void ensureFreshToken();
}

const mainMarginLeft = computed(() => (sidebarCollapsed.value ? '64px' : '220px'));
// Topbar is 56px. Impersonation banner is 36px directly below it. Push the
// app layout down so router-view content never slides under either of them.
const appLayoutPaddingTop = computed(() => (authStore.isImpersonated ? '92px' : '56px'));

const noAccess = computed(
  () =>
    authStore.roleLoaded &&
    authStore.role === 'user' &&
    !authStore.hasKeys &&
    !authStore.hasOwnedAccounts &&
    !authStore.hasManagedAccounts &&
    !authStore.hasMemberAccounts
);

const showLoading = computed(
  () => !initializeDto.hasError && (initializeDto.isLoading || !minTimeElapsed.value)
);

const accountWelcomeVisible = ref(false);
const ragAnnouncementVisible = ref(false);
const ragDocsUrl = getRagDocsUrl();

// Onboarding for a brand-new user's first account. It lives in the shell
// rather than on My Accounts so it lands wherever the user actually arrives
// first — the Dashboard, My Keys, a deep link — instead of only on the one
// page that used to host it. No creation timestamp is involved: /v1/auth/me
// carries none, and the one-shot claim already limits this to a single
// showing per user. The dialog itself is mounted inside the logged-in
// branch, so it can only surface once the shell is on screen.
function maybeShowAccountWelcome() {
  if (!welcomeAllowed(authStore.isImpersonated)) return;
  // Their *first* account — anyone managing more than one is past onboarding.
  if (authStore.managedAccounts.length !== 1) return;
  if (!claimAccountWelcome(authStore.kerberos)) return;

  accountWelcomeVisible.value = true;
}

// One-shot announcement for the knowledge base feature. Three gates, in the
// order that costs least: never stacked on top of the account welcome (that
// one is the new user's first impression, and two modals at once is neither),
// never for someone with no Parley access at all, and never unless the
// platform-wide Knowledge Base switch is on — there is nothing to announce on
// a stage where the feature is dark. The claim is taken *last* so a user who
// is skipped for any of those reasons still gets the announcement on a later
// load, once the switch is on and the welcome is out of the way.
async function maybeShowRagAnnouncement() {
  if (accountWelcomeVisible.value) return;
  if (noAccess.value) return;
  if (!welcomeAllowed(authStore.isImpersonated)) return;
  // Peek before the fetch: almost every load is by someone who has already
  // seen this, and there is no reason to spend a request on them.
  if (ragAnnouncementSeen(authStore.kerberos)) return;

  // Fails closed on an unreachable endpoint, and shares its request with the
  // router guard / tab bars that read the same flag.
  if (!(await fetchKnowledgeBaseEnabled())) return;
  if (!claimRagAnnouncement(authStore.kerberos)) return;

  ragAnnouncementVisible.value = true;
}

onMounted(async () => {
  const minTimer = setTimeout(() => {
    minTimeElapsed.value = true;
  }, 800);

  try {
    await AuthProviderV2.configure(STAGE_CONFIG);

    if (AuthProviderV2.getState() === AuthenticationState.ERROR) {
      console.error('Error configuring auth');
      initializeDto.hasError = true;
      initializeDto.isLoading = false;
      clearTimeout(minTimer);
      return;
    }

    // Decide on the *definitive* Okta auth status, not the synchronous
    // getState(): getState() can sit on 'loading' transiently, and signIn()
    // leaves it stale ('unauthenticated') when the Okta session is in fact
    // already valid — both of which used to strand the splash on
    // "Authenticating…". isAuthenticated() asks okta-auth-js directly.
    let authed = await AuthProviderV2.isAuthenticated();
    if (!authed) {
      // No session — kick off the Okta redirect. This normally navigates away
      // (so nothing below runs); it only resolves in-place when the session
      // turns out to be valid, so we re-check rather than assume.
      await AuthProviderV2.signIn();
      authed = await AuthProviderV2.isAuthenticated();
    }

    if (authed) {
      loadingStatus.value = 'Loading your workspace...';
      await authStore.initializeFromToken();
      await notificationStore.getCurrentUserPendingApprovals();
      await notificationStore.getSupervisorPendingApprovals();
      await notificationStore.getPendingApprovals();
      await notificationStore.getDormantKeyPausedKeys();
      isLoggedIn.value = true;
      initializeDto.isLoading = false;
      initializeDto.hasAccess = true;
      initializeDto.hasError = false;
      // initializeFromToken awaits fetchRole, so the account summaries and the
      // server's view of the kerb are both settled by here.
      maybeShowAccountWelcome();
      await maybeShowRagAnnouncement();
    } else {
      // Authenticated check failed and we didn't redirect away — surface the
      // sign-in screen instead of an endless spinner.
      initializeDto.isLoading = false;
      sessionExpired.value = true;
    }
  } catch (error) {
    console.error('Error initializing auth provider:', error);
    initializeDto.hasError = true;
    initializeDto.hasAccess = false;
    initializeDto.isLoading = false;
    isLoggedIn.value = false;
    clearTimeout(minTimer);
  }
});
</script>

<template>
  <ConfirmDialog />
  <CustomToast ref="customToastRef" />
  <OfflineIdentityPicker v-if="IS_OFFLINE" />

  <div v-if="initializeDto.hasError" class="error-screen">
    <h2>Error Initializing Application</h2>
    <p>There was an error during application initialization. Please try again later.</p>
  </div>

  <Transition name="fade">
    <div v-if="showLoading && !initializeDto.hasError" class="loading-screen">
      <img
        :src="mitLogo"
        alt="MIT"
        class="loading-logo"
        :class="{ 'loading-logo--inverted': mitLogoInverted }"
      />
      <span class="loading-brand">Parley Platform</span>
      <div class="loading-bar-track">
        <div class="loading-bar-fill" />
      </div>
      <p class="loading-status">{{ loadingStatus }}</p>
    </div>
  </Transition>

  <div
    v-if="sessionExpired && !showLoading && !initializeDto.hasError"
    class="session-expired-screen"
  >
    <img
      :src="mitLogo"
      alt="MIT"
      class="loading-logo"
      :class="{ 'loading-logo--inverted': mitLogoInverted }"
    />
    <span class="loading-brand">Parley Platform</span>
    <h2 class="session-expired-heading">Your session has expired</h2>
    <p class="session-expired-message">Sign in again to continue.</p>
    <button class="sign-in-btn" type="button" :disabled="signingIn" @click="handleSignIn">
      {{ signingIn ? 'Signing in...' : 'Sign in' }}
    </button>
  </div>

  <template v-if="isLoggedIn && !showLoading && !sessionExpired">
    <div
      v-if="authStore.isImpersonated"
      class="impersonation-banner"
      role="status"
      aria-live="polite"
    >
      <i class="pi pi-user-edit banner-icon" />
      <span class="banner-text">
        Impersonating
        <strong>{{ authStore.displayName }}</strong>
        <span class="banner-kerb">({{ authStore.kerberos }})</span>
      </span>
      <span class="banner-actor">
        by {{ authStore.impersonatedBy }} — end this session at impersonation.atlas-apps.mit.edu
      </span>
    </div>
    <AppHeader :style="{ top: authStore.isImpersonated ? '36px' : '0' }" />
    <div class="app-layout" :style="{ paddingTop: appLayoutPaddingTop }">
      <AppSidebar
        v-model:collapsed="sidebarCollapsed"
        :style="{
          top: authStore.isImpersonated ? '92px' : '56px',
          height: authStore.isImpersonated ? 'calc(100vh - 92px)' : 'calc(100vh - 56px)',
        }"
      />
      <div v-if="noAccess" class="no-access">
        <div class="no-access-card">
          <i class="pi pi-lock no-access-icon" aria-hidden="true" />
          <h1 class="no-access-title">You don't have Parley access yet</h1>
          <p class="no-access-text">
            You're signed in, but there is no Parley account or API key linked to your Kerberos
            identity. If you were expecting access, email the Parley team at
            <a class="no-access-email" :href="PARLEY_SUPPORT_MAILTO">{{ PARLEY_SUPPORT_EMAIL }}</a>
            and we'll get you set up.
          </p>
          <div class="no-access-actions">
            <a class="no-access-btn" :href="PARLEY_SUPPORT_MAILTO">
              <i class="pi pi-envelope" aria-hidden="true" />
              <span>Email Parley support</span>
            </a>
            <a class="no-access-link" :href="PARLEY_DOCS_URL" target="_blank" rel="noopener">
              Read the documentation
            </a>
          </div>
        </div>
      </div>
      <main v-else class="main-content" :style="{ marginLeft: mainMarginLeft }">
        <router-view />
      </main>
    </div>
    <MyAccountsWelcomeDialog v-model:visible="accountWelcomeVisible" :docs-url="PARLEY_DOCS_URL" />
    <RagAnnouncementDialog v-model:visible="ragAnnouncementVisible" :docs-url="ragDocsUrl" />
  </template>
</template>

<style scoped>
.loading-screen {
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  width: 100vw;
  background: var(--surface);
  z-index: 9999;
}

.loading-logo {
  width: 72px;
  height: auto;
  filter: brightness(0);
}

/* After .loading-logo so it wins: forced black, then inverted to white. */
.loading-logo--inverted {
  filter: brightness(0) invert(1);
}

.loading-brand {
  margin-top: 16px;
  font-size: 28px;
  font-weight: 700;
  color: var(--ink);
  letter-spacing: -0.5px;
}

.loading-bar-track {
  margin-top: 32px;
  width: 200px;
  height: 3px;
  background: var(--surface-3);
  border-radius: 2px;
  overflow: hidden;
}

.loading-bar-fill {
  width: 40%;
  height: 100%;
  background: linear-gradient(90deg, var(--brand), var(--brand-2), var(--brand));
  background-size: 200% 100%;
  border-radius: 2px;
  animation: loading-sweep 1.5s ease-in-out infinite;
}

@keyframes loading-sweep {
  0% {
    transform: translateX(-100%);
    background-position: 0% 0%;
  }
  50% {
    background-position: 100% 0%;
  }
  100% {
    transform: translateX(350%);
    background-position: 0% 0%;
  }
}

.loading-status {
  margin-top: 16px;
  font-size: 14px;
  color: var(--ink-4);
  letter-spacing: 0.01em;
}

.fade-leave-active {
  transition: opacity 0.4s ease;
}

.fade-leave-to {
  opacity: 0;
}

.error-screen {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  text-align: center;
  padding: 32px;
}

.session-expired-screen {
  position: fixed;
  top: 0;
  left: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  width: 100vw;
  background: var(--surface);
  z-index: 9999;
  padding: 32px;
}

.session-expired-heading {
  margin-top: 32px;
  font-size: 20px;
  font-weight: 600;
  color: var(--ink);
}

.session-expired-message {
  margin-top: 8px;
  font-size: 14px;
  color: var(--ink-4);
}

.sign-in-btn {
  margin-top: 24px;
  padding: 10px 20px;
  font-size: 14px;
  font-weight: 600;
  color: white;
  background: var(--accent);
  border: none;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s ease;
}

.sign-in-btn:hover:enabled {
  background: var(--accent-2);
}

.sign-in-btn:disabled {
  opacity: 0.7;
  cursor: default;
}

.app-layout {
  display: flex;
  min-height: calc(100vh - 56px);
}

/* Slim blue band pinned to the very top of the viewport whenever an
   impersonated session is active — sits above the topbar so it's the first
   thing the user sees. When visible, AppHeader picks up .app-topbar-shifted
   (top: 36px) to make room. */
.impersonation-banner {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  height: 36px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 0 24px;
  background: rgba(25, 102, 255, 0.1);
  border-bottom: 1px solid rgba(25, 102, 255, 0.25);
  color: var(--blue);
  font-size: 12.5px;
  z-index: 250;
}

.banner-icon {
  font-size: 13px;
}

.banner-text {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.banner-text strong {
  font-weight: 600;
}

.banner-kerb {
  color: rgba(25, 102, 255, 0.75);
  font-size: 11.5px;
}

/* Informational only — parley has no way to end an Okta-issued impersonation
   session itself; that has to happen at impersonation.atlas-apps.mit.edu. */
.banner-actor {
  margin-left: auto;
  font-size: 11.5px;
  color: rgba(25, 102, 255, 0.75);
}

.main-content {
  flex: 1;
  min-width: 0;
  padding: 24px 32px;
  transition: margin-left 0.2s ease;
}

.no-access {
  min-height: 60vh;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 24px;
  width: 100%;
}

.no-access-card {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 12px;
  max-width: 480px;
  background: var(--surface);
  border: 1px solid var(--border-2);
  border-radius: 14px;
  padding: 48px 40px;
}

.no-access-icon {
  font-size: 32px;
  color: var(--ink-4);
  margin-bottom: 4px;
}

.no-access-title {
  margin: 0;
  font-size: 22px;
  font-weight: 700;
  color: var(--ink);
  letter-spacing: -0.01em;
}

.no-access-text {
  margin: 0;
  font-size: 14px;
  line-height: 1.6;
  color: var(--ink-2);
}

.no-access-email {
  color: var(--accent, var(--accent));
  font-weight: 600;
  text-decoration: underline;
  text-underline-offset: 2px;
  white-space: nowrap;
}

.no-access-actions {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-top: 12px;
}

.no-access-btn {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 9px 18px;
  border-radius: 8px;
  background: var(--accent, var(--accent));
  color: #fff;
  font-size: 13.5px;
  font-weight: 600;
  text-decoration: none;
  transition: opacity 0.15s;
}

.no-access-btn:hover {
  opacity: 0.9;
}

.no-access-link {
  font-size: 13px;
  color: var(--ink-2);
  text-decoration: underline;
  text-underline-offset: 2px;
}

.no-access-link:hover {
  color: var(--ink);
}
</style>
